import { useEffect } from 'react';

import { PROJECT_SUMMARY_V2 } from '../../../../shared/featureFlags/flagNames';
import { useLocation, useParams } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { queryClient, useQuery } from '../../../ReactQuery';
import type { FullRevisionResponse } from '../../../../shared/types/routes/revisions';
import type { RevisionRouteParams } from '../../../components/projects/projectDashboardTypes';

import { getQueryKey as getFullQueryKey, getRevision } from './useRevision';
import { getMinimalQueryKey, getMinimalRevision } from './useRevisionMinimal';

const POLL_INTERVAL = 7000;
// Revision data doesn't change that frequently, and by keeping this high (1 min), we can save some queries and make the app feel snappier when navigating back to a revision.
const STALE_TIME = 60_000;

/**
 * Determine if the current pathname is the project summary page.
 * The summary page URL ends with the revisionId as a path segment — sub-pages
 * append additional segments after it.
 *
 * Decodes the pathname before comparing so that percent-encoded characters
 * (e.g. %24 for $) match the decoded revisionId from route params.
 *
 * Checks that the decoded pathname ends with `/<revisionId>` to avoid false
 * matches against the revision string appearing as a substring within other
 * path segments (e.g. revision "a" inside "branch", or "iss" inside "issues").
 *
 * @example
 * isOnSummaryPage('/projects/custom%2B1%2Ftest/refs/branch/master/1234567890') // true
 * isOnSummaryPage('/projects/custom%2B1%2Ftest/refs/branch/master/1234567890/issues') // false
 * isOnSummaryPage('/projects/custom%2B1%2Ftest/refs/branch/master/1234567890/browse/dependencies') // false
 * isOnSummaryPage('/projects/sbom%2B19518%2Fcumulocity2026/refs/branch/master/a') // true
 * isOnSummaryPage('/projects/sbom%2B19518%2Fcumulocity2026/refs/branch/master/a/issues') // false
 * isOnSummaryPage('/projects/binary%2B1%2Ftest/refs/branch/master/v1.0.0%241761258793873') // true
 */
function isOnSummaryPage(pathname: string, revisionId?: string): boolean {
  if (!revisionId) {
    return false;
  }
  return decodeURIComponent(pathname).endsWith(`/${revisionId}`);
}

/** Returns true if the error is an HTTP 4xx response (permanent client error). */
function isClientError(error: unknown): boolean {
  const status = (error as Response)?.status;
  return !!status && status >= 400 && status < 500;
}

/**
 * Fetches the current revision based on URL params, with automatic polling
 * when the revision is in an unsteady state.
 *
 * Like `useCurrentProject`, it reads URL params to determine the resource to
 * fetch. It additionally handles minimal vs. full endpoint selection, as well as
 * automatic polling when the revision is in an unsteady state.
 *
 * Endpoint selection: the summary page uses the full endpoint while the revision
 * is still building (so it can read server-computed `isSteady`) and the lighter
 * minimal endpoint once steady; every other route always uses the minimal endpoint.
 *
 * Polling behavior:
 * - Full endpoint: polls every 7s until server-computed `isSteady` is true
 * - Minimal endpoint: polls every 7s until `resolved`
 * - Once steady, polling stops automatically
 *
 * NOTE: The old Reflux RevisionStore passed `checkDependencyCache=true` and
 * sent a `fossa-skip-request-cache: 1` header. Backend investigation confirms
 * that neither is read by any server code — both are dead. We intentionally
 * do not carry them forward.
 */
export function useCurrentRevision() {
  const params = useParams<RevisionRouteParams>();
  const location = useLocation();
  const isProjectSummaryV2Enabled = useIsFeatureEnabled(PROJECT_SUMMARY_V2);

  const revisionLocator = `${params.project_id}$${params.revision_id}`;
  const isSummaryPage = isOnSummaryPage(location.pathname, params.revision_id);
  //  If there is a latest revision on the project, then this will always be set via the ProjectContainer.
  //  If for some reason this there is no revision, then we handle it being falsey by not fetching anything.
  const shouldFetch = !!params.revision_id;

  /*
   * Only the full endpoint computes `isSteady` (the minimal route never calls
   * getSteadyState). The summary page needs `isSteady` to tell whether the
   * revision is still building, so use the full endpoint until the revision is
   * steady, then fall back to the lighter minimal endpoint. `getQueryData` reads
   * the full cache, which persists once fetched, so steadiness stays sticky and
   * we never flip back to full once the revision has been seen steady. Under V1
   * (flag off) the summary page always used full, which this preserves.
   */
  const cachedFullRevision = queryClient.getQueryData<FullRevisionResponse>(getFullQueryKey(revisionLocator));
  const isKnownSteady = cachedFullRevision?.isSteady === true;
  const useFullEndpoint = isSummaryPage && (!isProjectSummaryV2Enabled || !isKnownSteady);

  // Full revision — with polling when unsteady.
  const fullRevision = useQuery<FullRevisionResponse>({
    queryKey: getFullQueryKey(revisionLocator),
    queryFn: () => getRevision(revisionLocator),
    enabled: shouldFetch && useFullEndpoint,
    staleTime: STALE_TIME,
    refetchInterval: (data, query) => {
      // Continue polling even before the first successful response so that
      // transient errors (network blips, brief 500s) can recover without
      // requiring a manual page refresh. Stop on permanent client errors.
      if (!data) {
        return isClientError(query.state.error) ? false : POLL_INTERVAL;
      }
      return data.isSteady ? false : POLL_INTERVAL;
    },
  });

  // Minimal revision — polls only until resolved
  const minimalRevision = useQuery({
    queryKey: getMinimalQueryKey(revisionLocator),
    queryFn: () => getMinimalRevision(revisionLocator),
    enabled: shouldFetch && !useFullEndpoint,
    staleTime: STALE_TIME,
    refetchInterval: (data, query) => {
      // Same rationale as above: poll through transient errors, stop on permanent ones.
      if (!data) {
        return isClientError(query.state.error) ? false : POLL_INTERVAL;
      }
      return data.resolved ? false : POLL_INTERVAL;
    },
  });

  /*
   * Seed the minimal cache with full revision data so that navigating from the
   * summary page to a sub-page (or switching to the minimal endpoint once the
   * revision is steady) skips a redundant minimal fetch within staleTime, and so
   * the switch carries `isSteady` across rather than losing it.
   */
  useEffect(() => {
    if (fullRevision.data) {
      queryClient.setQueryData(getMinimalQueryKey(revisionLocator), fullRevision.data);
    }
  }, [fullRevision.data, revisionLocator]);

  const activeQuery = useFullEndpoint ? fullRevision : minimalRevision;

  return {
    ...activeQuery,
    isMinimal: !useFullEndpoint,
    isFullRevisionLoaded: useFullEndpoint && !!fullRevision.data,
    isSteady: isKnownSteady,
  };
}
